import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { stopType } from '#/prop-types';
import DetailViewHeader from '#/shared/components/DetailViewHeader';
import GTM_HANDLES from '#/shared/gtm-handles';
import StopDetailContainerSkeleton from '#/shared/components/Skeletons/StopDetailContainerSkeletons';
import useTriMetStaticPageLinks from '#/shared/hooks/useTriMetStaticPageLinks';
import PopoverMenu from '#/components/PopoverMenu';
import * as Styled from './styled';

function StopDetailHeader({
  stop,
  navigateToTripPlannerTo,
  navigateToTripPlannerFrom,
  onStopAmenitiesClick,
  surveyLink,
}) {
  const { getStopScheduleLink } = useTriMetStaticPageLinks();
  const descriptionElement = stopData => {
    if (!stopData) {
      return <StopDetailContainerSkeleton />;
    }

    return stopData.desc || <StopDetailContainerSkeleton />;
  };
  const stopScheduleLink = getStopScheduleLink(stop?.id);

  const menuItems = [
    {
      stringsId: 'detailview.schedule',
      href: stopScheduleLink,
      type: 'link',
      dataGtm: GTM_HANDLES.stop.stopScheduleLink,
    },
    {
      stringsId: 'detailview.amenities',
      onSelect: onStopAmenitiesClick,
      type: 'button',
      dataGtm: GTM_HANDLES.stop.stopAmenitiesButton,
    },
  ];
  return (
    <DetailViewHeader
      title={
        <Styled.TitleWrapper>
          <Styled.TitleContainer role="heading" aria-level={2}>
            {descriptionElement(stop)}
          </Styled.TitleContainer>
          <PopoverMenu menuItems={menuItems} />
        </Styled.TitleWrapper>
      }
      icon={<Styled.StopStationIconSolid />}
      actions={
        <Styled.ActionsWrapper>
          <Styled.TripButtonsWrapper>
            <Styled.TripButton
              onClick={navigateToTripPlannerTo}
              data-gtm={GTM_HANDLES.stop.stopDetailTripButtonGoHere}
            >
              <FormattedMessage id="detailview.gohere" />
            </Styled.TripButton>
            <Styled.TripButton
              onClick={navigateToTripPlannerFrom}
              data-gtm={GTM_HANDLES.stop.stopDetailTripButtonStartHere}
            >
              <FormattedMessage id="detailview.starthere" />
            </Styled.TripButton>
          </Styled.TripButtonsWrapper>
          <Styled.ShareFeedbackLink
            href={surveyLink}
            target="_blank"
            rel="noopener"
            data-gtm={GTM_HANDLES.stop.stopLeaveFeedbackLink}
          >
            <Styled.ShareFeedbackLinkIcon aria-hidden>
              <Styled.ShareFeedbackIcon />
            </Styled.ShareFeedbackLinkIcon>
            <Styled.ShareFeedbackLinkText>
              <FormattedMessage id="detailview.share-feedback" />
            </Styled.ShareFeedbackLinkText>
          </Styled.ShareFeedbackLink>
        </Styled.ActionsWrapper>
      }
    />
  );
}

StopDetailHeader.propTypes = {
  /** Stop Object that provides all meta info about a given stop */
  stop: stopType,
  /**
   * Function that triggers the action to navigate to the Trip Planner with the
   * stop ID as the "To" location
   */
  navigateToTripPlannerTo: PropTypes.func.isRequired,
  /**
   * Function that triggers the action to navigate to the Trip Planner with the
   * stop ID as the "From" location
   */
  navigateToTripPlannerFrom: PropTypes.func.isRequired,
  /** String that represents the link to the feedback survey */
  surveyLink: PropTypes.string.isRequired,
  /** Function that is executed when the amenities button is clicked */
  onStopAmenitiesClick: PropTypes.func.isRequired,
};

StopDetailHeader.defaultProps = {
  stop: null,
};

export default StopDetailHeader;
